import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createTextVNode as _createTextVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "sensor-extractions-view" }
const _hoisted_2 = { class: "d-flex align-center" }
const _hoisted_3 = { class: "text-h4" }
const _hoisted_4 = { class: "d-flex align-center" }
const _hoisted_5 = { class: "text-h4" }
const _hoisted_6 = { class: "d-flex align-center" }
const _hoisted_7 = { class: "text-h4" }
const _hoisted_8 = { class: "d-flex align-center" }
const _hoisted_9 = { class: "text-h4" }
const _hoisted_10 = { class: "d-flex flex-column" }
const _hoisted_11 = { class: "text-caption" }
const _hoisted_12 = { class: "d-flex flex-column" }

import { ref, onMounted, computed, reactive } from 'vue'
import { useSensorExtractionStore } from './store'
import { SensorExtraction } from './models/sensorExtraction'


export default /*@__PURE__*/_defineComponent({
  __name: 'SensorExtractionsView',
  setup(__props) {

const sensorExtractionStore = useSensorExtractionStore()
const loading = ref(false)
const bulkSyncLoading = ref(false)
const selectedSensorSettingId = ref<string | null>(null)
const selectedType = ref<string | null>(null)
const statusFilter = ref<string | null>(null)
const selectedItems = ref<string[]>([])
const syncLoading = reactive<Record<string, boolean>>({})
const toggleLoading = reactive<Record<string, boolean>>({})

const headers = [
  { title: 'ID', key: 'id' },
  { title: 'Config ID', key: 'sensorSettingId' },
  { title: 'Tipo', key: 'type' },
  { title: 'Setting ID', key: 'settingId' },
  { title: 'Intervalo', key: 'pollingInterval' },
  { title: 'Última Sync', key: 'lastSyncAt' },
  { title: 'Próxima Sync', key: 'nextSync' },
  { title: 'Activo', key: 'active' },
  { title: 'Estado', key: 'status' },
  { title: 'Acciones', key: 'actions', sortable: false },
]

// Opciones para los filtros
const sensorSettingOptions = ref([
  { title: 'Sensor Config 1', value: 'sensorConfig1' },
  { title: 'Sensor Config 2', value: 'sensorConfig2' },
  { title: 'Sensor Config 3', value: 'sensorConfig3' },
])

const typeOptions = ref([
  { title: 'FTP', value: 'ftp' },
  { title: 'API', value: 'api' },
  { title: 'Database', value: 'database' },
  { title: 'MQTT', value: 'mqtt' },
  { title: 'Socket', value: 'socket' },
])

const statusOptions = ref([
  { title: 'Activo', value: 'active' },
  { title: 'Inactivo', value: 'inactive' },
  { title: 'Atrasado', value: 'overdue' },
  { title: 'Sincronizado', value: 'synced' },
])

const sensorExtractionsList = computed(() => {
  let list = sensorExtractionStore.sensorExtractions
  
  if (selectedSensorSettingId.value) {
    list = list.filter(item => item.sensorSettingId === selectedSensorSettingId.value)
  }
  
  if (selectedType.value) {
    list = list.filter(item => item.type === selectedType.value)
  }
  
  if (statusFilter.value) {
    switch (statusFilter.value) {
      case 'active':
        list = list.filter(item => item.active)
        break
      case 'inactive':
        list = list.filter(item => !item.active)
        break
      case 'overdue':
        list = list.filter(item => item.isOverdue())
        break
      case 'synced':
        list = list.filter(item => !item.isOverdue() && item.active)
        break
    }
  }
  
  return list
})

// Estadísticas computadas
const totalExtractions = computed(() => sensorExtractionStore.sensorExtractions.length)
const activeCount = computed(() => sensorExtractionStore.sensorExtractions.filter(item => item.active).length)
const overdueCount = computed(() => sensorExtractionStore.sensorExtractions.filter(item => item.isOverdue()).length)
const recentlySyncedCount = computed(() => {
  const thirtyMinutesAgo = new Date()
  thirtyMinutesAgo.setMinutes(thirtyMinutesAgo.getMinutes() - 30)
  return sensorExtractionStore.sensorExtractions.filter(item => 
    new Date(item.lastSyncAt) >= thirtyMinutesAgo
  ).length
})

const formatInterval = (minutes: number) => {
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`
}

const formatDateTime = (date: Date | string) => {
  return new Date(date).toLocaleString('es-ES', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const getTypeColor = (type: string) => {
  const colors: Record<string, string> = {
    ftp: 'blue',
    api: 'green',
    database: 'purple',
    mqtt: 'orange',
    socket: 'red'
  }
  return colors[type] || 'grey'
}

const getTypeIcon = (type: string) => {
  const icons: Record<string, string> = {
    ftp: 'mdi-folder-network',
    api: 'mdi-api',
    database: 'mdi-database',
    mqtt: 'mdi-message-processing',
    socket: 'mdi-power-socket'
  }
  return icons[type] || 'mdi-help-circle'
}

const getStatusColor = (item: SensorExtraction) => {
  if (!item.active) return 'grey'
  if (item.isOverdue()) return 'error'
  return 'success'
}

const getStatusIcon = (item: SensorExtraction) => {
  if (!item.active) return 'mdi-pause'
  if (item.isOverdue()) return 'mdi-clock-alert'
  return 'mdi-check-circle'
}

const getStatusText = (item: SensorExtraction) => {
  if (!item.active) return 'Inactivo'
  if (item.isOverdue()) return 'Atrasado'
  return 'Al día'
}

const refreshData = async () => {
  loading.value = true
  try {
    await sensorExtractionStore.getSensorExtractions()
  } finally {
    loading.value = false
  }
}

const filterBySensorSetting = async (sensorSettingId: string | null) => {
  if (sensorSettingId) {
    loading.value = true
    try {
      await sensorExtractionStore.getSensorExtractionsBySensorSettingId(sensorSettingId)
    } finally {
      loading.value = false
    }
  } else {
    refreshData()
  }
}

const filterByType = async (type: string | null) => {
  if (type) {
    loading.value = true
    try {
      await sensorExtractionStore.getSensorExtractionsByType(type)
    } finally {
      loading.value = false
    }
  } else {
    refreshData()
  }
}

const filterByStatus = () => {
  // El filtrado por estado se maneja en el computed
}

const addItem = () => {
  console.log('Add new sensor extraction')
}

const editItem = (item: SensorExtraction) => {
  console.log('Edit item:', item)
}

const deleteItem = (item: SensorExtraction) => {
  console.log('Delete item:', item)
}

const syncNow = async (item: SensorExtraction) => {
  syncLoading[item.id] = true
  try {
    await sensorExtractionStore.syncSensorExtraction(item.id)
    await refreshData()
  } catch (error) {
    console.error('Error syncing extraction:', error)
  } finally {
    syncLoading[item.id] = false
  }
}

const toggleStatus = async (item: SensorExtraction) => {
  toggleLoading[item.id] = true
  try {
    await sensorExtractionStore.toggleSensorExtractionStatus(item.id)
    await refreshData()
  } catch (error) {
    console.error('Error toggling status:', error)
  } finally {
    toggleLoading[item.id] = false
  }
}

const bulkSync = async () => {
  if (selectedItems.value.length === 0) return
  
  bulkSyncLoading.value = true
  try {
    await sensorExtractionStore.bulkSyncSensorExtractions(selectedItems.value)
    selectedItems.value = []
    await refreshData()
  } catch (error) {
    console.error('Error bulk syncing extractions:', error)
  } finally {
    bulkSyncLoading.value = false
  }
}

onMounted(() => {
  refreshData()
})

return (_ctx: any,_cache: any) => {
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_toolbar_title = _resolveComponent("v-toolbar-title")!
  const _component_v_divider = _resolveComponent("v-divider")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_toolbar = _resolveComponent("v-toolbar")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_switch = _resolveComponent("v-switch")!
  const _component_v_tooltip = _resolveComponent("v-tooltip")!
  const _component_v_data_table = _resolveComponent("v-data-table")!
  const _component_v_container = _resolveComponent("v-container")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_v_container, null, {
      default: _withCtx(() => [
        _createVNode(_component_v_row, null, {
          default: _withCtx(() => [
            _createVNode(_component_v_col, { cols: "12" }, {
              default: _withCtx(() => _cache[4] || (_cache[4] = [
                _createElementVNode("h1", null, "Sensor Extractions", -1),
                _createElementVNode("p", null, "Gestión de extracciones de datos de sensores", -1)
              ])),
              _: 1
            })
          ]),
          _: 1
        }),
        _createVNode(_component_v_row, null, {
          default: _withCtx(() => [
            _createVNode(_component_v_col, {
              cols: "12",
              md: "3"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_card, {
                  color: "primary",
                  variant: "tonal"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_card_text, null, {
                      default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_2, [
                          _createVNode(_component_v_icon, {
                            size: "40",
                            class: "mr-3"
                          }, {
                            default: _withCtx(() => _cache[5] || (_cache[5] = [
                              _createTextVNode("mdi-sensors")
                            ])),
                            _: 1
                          }),
                          _createElementVNode("div", null, [
                            _createElementVNode("div", _hoisted_3, _toDisplayString(totalExtractions.value), 1),
                            _cache[6] || (_cache[6] = _createElementVNode("div", { class: "text-caption" }, "Total Extracciones", -1))
                          ])
                        ])
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(_component_v_col, {
              cols: "12",
              md: "3"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_card, {
                  color: "success",
                  variant: "tonal"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_card_text, null, {
                      default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_4, [
                          _createVNode(_component_v_icon, {
                            size: "40",
                            class: "mr-3"
                          }, {
                            default: _withCtx(() => _cache[7] || (_cache[7] = [
                              _createTextVNode("mdi-check-circle")
                            ])),
                            _: 1
                          }),
                          _createElementVNode("div", null, [
                            _createElementVNode("div", _hoisted_5, _toDisplayString(activeCount.value), 1),
                            _cache[8] || (_cache[8] = _createElementVNode("div", { class: "text-caption" }, "Activas", -1))
                          ])
                        ])
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(_component_v_col, {
              cols: "12",
              md: "3"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_card, {
                  color: "warning",
                  variant: "tonal"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_card_text, null, {
                      default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_6, [
                          _createVNode(_component_v_icon, {
                            size: "40",
                            class: "mr-3"
                          }, {
                            default: _withCtx(() => _cache[9] || (_cache[9] = [
                              _createTextVNode("mdi-clock-alert")
                            ])),
                            _: 1
                          }),
                          _createElementVNode("div", null, [
                            _createElementVNode("div", _hoisted_7, _toDisplayString(overdueCount.value), 1),
                            _cache[10] || (_cache[10] = _createElementVNode("div", { class: "text-caption" }, "Atrasadas", -1))
                          ])
                        ])
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(_component_v_col, {
              cols: "12",
              md: "3"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_card, {
                  color: "info",
                  variant: "tonal"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_card_text, null, {
                      default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_8, [
                          _createVNode(_component_v_icon, {
                            size: "40",
                            class: "mr-3"
                          }, {
                            default: _withCtx(() => _cache[11] || (_cache[11] = [
                              _createTextVNode("mdi-sync")
                            ])),
                            _: 1
                          }),
                          _createElementVNode("div", null, [
                            _createElementVNode("div", _hoisted_9, _toDisplayString(recentlySyncedCount.value), 1),
                            _cache[12] || (_cache[12] = _createElementVNode("div", { class: "text-caption" }, "Recién Sincronizadas", -1))
                          ])
                        ])
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        }),
        _createVNode(_component_v_row, null, {
          default: _withCtx(() => [
            _createVNode(_component_v_col, {
              cols: "12",
              md: "3"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_select, {
                  modelValue: selectedSensorSettingId.value,
                  "onUpdate:modelValue": [
                    _cache[0] || (_cache[0] = ($event: any) => ((selectedSensorSettingId).value = $event)),
                    filterBySensorSetting
                  ],
                  items: sensorSettingOptions.value,
                  label: "Filtrar por Configuración",
                  clearable: ""
                }, null, 8, ["modelValue", "items"])
              ]),
              _: 1
            }),
            _createVNode(_component_v_col, {
              cols: "12",
              md: "3"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_select, {
                  modelValue: selectedType.value,
                  "onUpdate:modelValue": [
                    _cache[1] || (_cache[1] = ($event: any) => ((selectedType).value = $event)),
                    filterByType
                  ],
                  items: typeOptions.value,
                  label: "Filtrar por Tipo",
                  clearable: ""
                }, null, 8, ["modelValue", "items"])
              ]),
              _: 1
            }),
            _createVNode(_component_v_col, {
              cols: "12",
              md: "3"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_select, {
                  modelValue: statusFilter.value,
                  "onUpdate:modelValue": [
                    _cache[2] || (_cache[2] = ($event: any) => ((statusFilter).value = $event)),
                    filterByStatus
                  ],
                  items: statusOptions.value,
                  label: "Filtrar por Estado",
                  clearable: ""
                }, null, 8, ["modelValue", "items"])
              ]),
              _: 1
            }),
            _createVNode(_component_v_col, {
              cols: "12",
              md: "3"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_btn, {
                  color: "secondary",
                  block: "",
                  onClick: bulkSync,
                  disabled: selectedItems.value.length === 0,
                  loading: bulkSyncLoading.value
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_icon, { left: "" }, {
                      default: _withCtx(() => _cache[13] || (_cache[13] = [
                        _createTextVNode("mdi-sync")
                      ])),
                      _: 1
                    }),
                    _createTextVNode(" Sincronizar Seleccionadas (" + _toDisplayString(selectedItems.value.length) + ") ", 1)
                  ]),
                  _: 1
                }, 8, ["disabled", "loading"])
              ]),
              _: 1
            })
          ]),
          _: 1
        }),
        _createVNode(_component_v_row, null, {
          default: _withCtx(() => [
            _createVNode(_component_v_col, { cols: "12" }, {
              default: _withCtx(() => [
                _createVNode(_component_v_data_table, {
                  modelValue: selectedItems.value,
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((selectedItems).value = $event)),
                  headers: headers,
                  items: sensorExtractionsList.value,
                  loading: loading.value,
                  "item-value": "id",
                  "show-select": "",
                  class: "elevation-1"
                }, {
                  top: _withCtx(() => [
                    _createVNode(_component_v_toolbar, { flat: "" }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_toolbar_title, null, {
                          default: _withCtx(() => _cache[14] || (_cache[14] = [
                            _createTextVNode("Lista de Extracciones de Sensores")
                          ])),
                          _: 1
                        }),
                        _createVNode(_component_v_divider, {
                          class: "mx-4",
                          inset: "",
                          vertical: ""
                        }),
                        _createVNode(_component_v_spacer),
                        _createVNode(_component_v_btn, {
                          color: "primary",
                          onClick: refreshData
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_icon, { left: "" }, {
                              default: _withCtx(() => _cache[15] || (_cache[15] = [
                                _createTextVNode("mdi-refresh")
                              ])),
                              _: 1
                            }),
                            _cache[16] || (_cache[16] = _createTextVNode(" Actualizar "))
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_v_btn, {
                          color: "success",
                          class: "ml-2",
                          onClick: addItem
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_icon, { left: "" }, {
                              default: _withCtx(() => _cache[17] || (_cache[17] = [
                                _createTextVNode("mdi-plus")
                              ])),
                              _: 1
                            }),
                            _cache[18] || (_cache[18] = _createTextVNode(" Agregar Extracción "))
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    })
                  ]),
                  "item.sensorSettingId": _withCtx(({ item }) => [
                    _createVNode(_component_v_chip, {
                      color: "blue",
                      variant: "outlined",
                      small: ""
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(item.sensorSettingId), 1)
                      ]),
                      _: 2
                    }, 1024)
                  ]),
                  "item.type": _withCtx(({ item }) => [
                    _createVNode(_component_v_chip, {
                      color: getTypeColor(item.type),
                      small: ""
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_icon, {
                          left: "",
                          size: "small"
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(getTypeIcon(item.type)), 1)
                          ]),
                          _: 2
                        }, 1024),
                        _createTextVNode(" " + _toDisplayString(item.type.toUpperCase()), 1)
                      ]),
                      _: 2
                    }, 1032, ["color"])
                  ]),
                  "item.pollingInterval": _withCtx(({ item }) => [
                    _createVNode(_component_v_chip, {
                      color: "indigo",
                      variant: "outlined",
                      small: ""
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(formatInterval(item.pollingInterval)), 1)
                      ]),
                      _: 2
                    }, 1024)
                  ]),
                  "item.lastSyncAt": _withCtx(({ item }) => [
                    _createElementVNode("div", _hoisted_10, [
                      _createElementVNode("span", _hoisted_11, _toDisplayString(formatDateTime(item.lastSyncAt)), 1),
                      _createVNode(_component_v_chip, {
                        color: item.isOverdue() ? 'error' : 'success',
                        size: "x-small"
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(item.getTimeSinceLastSync()), 1)
                        ]),
                        _: 2
                      }, 1032, ["color"])
                    ])
                  ]),
                  "item.nextSync": _withCtx(({ item }) => [
                    _createElementVNode("div", _hoisted_12, [
                      _createElementVNode("span", {
                        class: _normalizeClass(["text-caption", { 'text-error': item.isOverdue() }])
                      }, _toDisplayString(formatDateTime(item.getNextSyncTime())), 3),
                      _createVNode(_component_v_chip, {
                        color: item.isOverdue() ? 'error' : 'info',
                        size: "x-small"
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(item.getTimeUntilNextSync()), 1)
                        ]),
                        _: 2
                      }, 1032, ["color"])
                    ])
                  ]),
                  "item.active": _withCtx(({ item }) => [
                    _createVNode(_component_v_switch, {
                      modelValue: item.active,
                      "onUpdate:modelValue": ($event: any) => ((item.active) = $event),
                      loading: toggleLoading[item.id],
                      color: "success",
                      onChange: ($event: any) => (toggleStatus(item)),
                      "hide-details": "",
                      density: "compact"
                    }, null, 8, ["modelValue", "onUpdate:modelValue", "loading", "onChange"])
                  ]),
                  "item.status": _withCtx(({ item }) => [
                    _createVNode(_component_v_chip, {
                      color: getStatusColor(item),
                      small: ""
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_icon, {
                          left: "",
                          size: "small"
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(getStatusIcon(item)), 1)
                          ]),
                          _: 2
                        }, 1024),
                        _createTextVNode(" " + _toDisplayString(getStatusText(item)), 1)
                      ]),
                      _: 2
                    }, 1032, ["color"])
                  ]),
                  "item.actions": _withCtx(({ item }) => [
                    _createVNode(_component_v_btn, {
                      size: "small",
                      icon: "",
                      variant: "text",
                      onClick: ($event: any) => (syncNow(item)),
                      loading: syncLoading[item.id],
                      disabled: !item.active
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_icon, null, {
                          default: _withCtx(() => _cache[19] || (_cache[19] = [
                            _createTextVNode("mdi-sync")
                          ])),
                          _: 1
                        }),
                        _createVNode(_component_v_tooltip, {
                          activator: "parent",
                          location: "top"
                        }, {
                          default: _withCtx(() => _cache[20] || (_cache[20] = [
                            _createTextVNode(" Sincronizar ahora ")
                          ])),
                          _: 1
                        })
                      ]),
                      _: 2
                    }, 1032, ["onClick", "loading", "disabled"]),
                    _createVNode(_component_v_btn, {
                      size: "small",
                      icon: "",
                      variant: "text",
                      onClick: ($event: any) => (editItem(item))
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_icon, null, {
                          default: _withCtx(() => _cache[21] || (_cache[21] = [
                            _createTextVNode("mdi-pencil")
                          ])),
                          _: 1
                        }),
                        _createVNode(_component_v_tooltip, {
                          activator: "parent",
                          location: "top"
                        }, {
                          default: _withCtx(() => _cache[22] || (_cache[22] = [
                            _createTextVNode(" Editar extracción ")
                          ])),
                          _: 1
                        })
                      ]),
                      _: 2
                    }, 1032, ["onClick"]),
                    _createVNode(_component_v_btn, {
                      size: "small",
                      icon: "",
                      variant: "text",
                      onClick: ($event: any) => (deleteItem(item))
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_icon, null, {
                          default: _withCtx(() => _cache[23] || (_cache[23] = [
                            _createTextVNode("mdi-delete")
                          ])),
                          _: 1
                        }),
                        _createVNode(_component_v_tooltip, {
                          activator: "parent",
                          location: "top"
                        }, {
                          default: _withCtx(() => _cache[24] || (_cache[24] = [
                            _createTextVNode(" Eliminar extracción ")
                          ])),
                          _: 1
                        })
                      ]),
                      _: 2
                    }, 1032, ["onClick"])
                  ]),
                  _: 1
                }, 8, ["modelValue", "items", "loading"])
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    })
  ]))
}
}

})